import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { track } from 'src/configs/amplitude';

/**
 * Loud on purpose (Haley, 15 Oct): a utility turned on for AP Push or Bill Automation is new, time-sensitive work.
 * Shows until someone on the team marks the changes seen; the split by option comes from the changes read.
 */
export const EnrolmentBanner = observer(_ref => {
  let unseen = _ref.unseen,
    onReview = _ref.onReview,
    onSeen = _ref.onSeen;
  const store = useStore();
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    split = _useState2[0],
    setSplit = _useState2[1];
  const _useState3 = useState({
      confirming: false,
      saving: false,
      error: ''
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    seen = _useState4[0],
    setSeen = _useState4[1];
  useEffect(() => {
    if (!unseen) return undefined;
    let cancelled = false;
    store.admin.getBillQueueChangesSplitAsync.run(30).then(resp => {
      var _ref2;
      const changes = (_ref2 = (resp === null || resp === void 0 ? void 0 : resp.response) || resp) === null || _ref2 === void 0 ? void 0 : _ref2.changes;
      if (cancelled || !Array.isArray(changes)) return;
      const on = changes.filter(c => !c.seen && (c.kind === 'TurnedOn' || c.kind === 'UtilityAdded'));
      setSplit({
        apPush: on.filter(c => c.option === 'ApPush').length,
        billAutomation: on.filter(c => c.option === 'BillAutomation').length,
        // What "mark as seen" covers: a utility turned on after this read still shows next time.
        through: on.reduce((newest, c) => !newest || c.changedOn > newest ? c.changedOn : newest, null)
      });
    });
    return () => {
      cancelled = true;
    };
  }, [store, unseen]);
  if (!unseen) return null;
  const handleSeen = async () => {
    setSeen(s => _objectSpread(_objectSpread({}, s), {}, {
      saving: true,
      error: ''
    }));
    await store.admin.markBillQueueChangesSeenAsync.run(split.through);
    if (store.admin.markBillQueueChangesSeenAsync.isError) {
      setSeen({
        confirming: true,
        saving: false,
        error: 'Could not mark these as seen. Try again.'
      });
      return;
    }
    setSeen({
      confirming: false,
      saving: false,
      error: ''
    });
    track('bill-queue-changes-seen', {
      count: unseen
    });
    onSeen();
  };
  const handleCancel = () => setSeen({
    confirming: false,
    saving: false,
    error: ''
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    sx: {
      marginBottom: '12px',
      alignItems: 'center',
      border: '2px solid',
      borderColor: 'warning.main',
      backgroundColor: 'warning.light',
      flexWrap: 'wrap',
      '& .MuiAlert-message': {
        flex: '1 1 220px',
        minWidth: 0
      },
      // Below the message on a phone: beside it the buttons squeezed the message to nothing.
      '& .MuiAlert-action': {
        marginLeft: {
          xs: 0,
          sm: 'auto'
        },
        paddingLeft: 0,
        width: {
          xs: '100%',
          sm: 'auto'
        }
      }
    },
    action: /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      spacing: 8
    }, /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      size: "small",
      onClick: onReview
    }, "Review changes"), /*#__PURE__*/React.createElement(Button, {
      variant: "actionButtonInvert",
      size: "small",
      onClick: () => setSeen(s => _objectSpread(_objectSpread({}, s), {}, {
        confirming: true
      }))
    }, "Mark as seen"))
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    component: "div"
  }, "".concat(unseen, " ").concat(unseen === 1 ? 'utility was' : 'utilities were', " turned on since your team last checked")), split ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, "AP Push: ".concat(split.apPush, " \xB7 Bill Automation: ").concat(split.billAutomation)) : null), /*#__PURE__*/React.createElement(Dialog, {
    open: seen.confirming,
    onClose: handleCancel,
    "aria-labelledby": "mark-seen-title"
  }, /*#__PURE__*/React.createElement(DialogTitle, {
    id: "mark-seen-title"
  }, "Mark as seen?"), /*#__PURE__*/React.createElement(DialogContent, null, /*#__PURE__*/React.createElement(Box, null, "Mark these ".concat(unseen, " changes as seen for the whole team? The banner goes away until the next utility is turned on.")), seen.error ? /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    sx: {
      marginTop: '12px'
    }
  }, seen.error) : null), /*#__PURE__*/React.createElement(DialogActions, null, /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    onClick: handleCancel
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    onClick: handleSeen,
    disabled: seen.saving || !(split !== null && split !== void 0 && split.through),
    autoFocus: true
  }, seen.saving ? 'Marking...' : 'Mark as seen'))));
});